import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { Capacitor } from '@capacitor/core';
import clsx from 'clsx';

import { useStyles as useAppButtonStyles } from '../../../../components/designe/buttons/app-button/AppButton.styles';
import { useGetAppVariables } from '../../../../redux/api/appConfigApi';
import {
  AppVariableKeys,
  getAppVariableValueInsensitive,
} from '../../../../types/interfaces/app-variable';
import { openExternalUrl } from '../../../../utils/platform/open-external-url';
import { useStyles } from './ExternalSupportBlock.styles';

const EXTERNAL_ICON_SX = { fontSize: 20 } as const;

function ExternalPlatformLinkButton(props: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { href, className, children } = props;
  const { classes: btnClasses } = useAppButtonStyles({
    variant: 'contained',
    color: 'secondary',
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (Capacitor.getPlatform() !== 'web') {
      e.preventDefault();
      void openExternalUrl(href);
    }
  };

  return (
    <Button
      component="a"
      variant="contained"
      color="secondary"
      className={clsx(btnClasses.button, className)}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      startIcon={<OpenInNewIcon sx={EXTERNAL_ICON_SX} />}>
      <span className={btnClasses.labelWrap}>{children}</span>
    </Button>
  );
}

export default function ExternalSupportBlock() {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: variables } = useGetAppVariables();

  const boostyUrl = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.boostyUrl,
  );
  const patreonUrl = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.patreonUrl,
  );

  if (!boostyUrl && !patreonUrl) {
    return null;
  }

  return (
    <Box className={classes.card}>
      <Typography variant="h2" component="h2" className={classes.title}>
        {t('subscriptionPlan.externalSupportTitle')}
      </Typography>
      <Typography variant="text-md" className={classes.subtitle}>
        {t('subscriptionPlan.externalSupportSubtitle')}
      </Typography>
      <Box className={classes.buttonsRow} marginTop={1}>
        {boostyUrl && (
          <ExternalPlatformLinkButton
            href={boostyUrl}
            className={classes.linkButton}>
            Boosty
          </ExternalPlatformLinkButton>
        )}
        {patreonUrl && (
          <ExternalPlatformLinkButton
            href={patreonUrl}
            className={classes.linkButton}>
            Patreon
          </ExternalPlatformLinkButton>
        )}
      </Box>
    </Box>
  );
}
